import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getCompositeFields, invalidateCompositeEntity,
  listCompositeRelationships, listEntityFields,
} from '../../../services';
import { getDataset } from '../../../services/datasets.js';
import { listAllEntities } from './useWidgetForEdit.js';
import { ALLOW_EXTENSION_MEMBER_FIELDS } from '../constants/compositeFields.js';
import { resolveMappedColumns } from '../utils/mappedColumns.js';

const EMPTY = Object.freeze([]);

/**
 * An entity's columns: its composite's **mapped fields** when it has a mapping,
 * else every column of the primary member (CT-5480).
 *
 * The mapping is the source — the builder starts from what the composite
 * exposes, each field with its origin, read from
 * `composite-entity/field-map/list`. A composite with no mapping falls back to
 * today's list, and a mapping that cannot be read must not take the Data step
 * down with it, so a failure falls back too.
 *
 * ## CT-5480 — where this stands (2026-10-01), for whoever picks it up next
 *
 * The ticket: the composite is the widget builder's root, and the builder
 * offers only the fields mapped for it in Data Studio (Entity → Fields), each
 * keeping its origin (member table + column) so the query engine can derive
 * the joins from the selected fields.
 *
 * Done in the front:
 *   - The entity picker lists composites (`composite-entity/list`); a card is
 *     keyed by `compositeEntityUuid`, its `primaryEntityUuid` is `entityRef`.
 *   - Columns come from `POST composite-entity/field-map/list`
 *     (`getCompositeFields` in `services/dataModel.js`) joined with each
 *     member's `field/list` for the metadata a mapping does not carry
 *     (`resolveMappedColumns` in `utils/mappedColumns.js`), with the origin
 *     shown in the column picker.
 *   - The fallback below. It is **not** temporary: a composite without a
 *     mapping is a valid state, and it keeps the primary's full list.
 *
 * Blocked on the backend (Andrey's engine and the minimal payload, still
 * being designed when this was written):
 *   - EXTENSION members' fields are **offered** — every mapped field is, by
 *     product decision (`ALLOW_EXTENSION_MEMBER_FIELDS`). The engine has not
 *     caught up: it validates against the PRIMARY member alone, and mapping
 *     a field does not authorise it. Measured 2026-10-02 on
 *     `QA Composite Entity`, whose mapping covers both members — the PRIMARY
 *     field saved (`DSW000`), the EXTENSION field was refused at `create`
 *     with `DSW018 Fields [...] are not fields of the chosen entity`. So a
 *     widget built on one cannot be saved until the engine derives the joins
 *     from each field's origin.
 *   - Sending each field's origin in the widget payload, and the explicit
 *     relationship path when there is more than one (Customer → Vehicles).
 *     Both belong in `buildWidgetPayload.js`, as the dataset contract
 *     ("contract 2") that has not been defined yet.
 *
 * Not front work: bulk field mapping, the alias-uniqueness rule (Data Studio
 * answers `DST142` on a duplicate) and the `parentComposite` proposal live in
 * Data Studio (`@aimsos/data-studio`, a separate package).
 *
 * Five composites in dev have a mapping (2026-10-02): `QA Composite Entity`
 * (7 fields, across a PRIMARY and an EXTENSION) and `QA Exhaustive Entity`
 * (5, same) exercise this path; `Employee` has exactly one mapped field, so
 * its Data step now offers one column where it used to offer 21. That is the
 * mapping being thin, not a bug here — map more fields in Data Studio
 * (Entity → Fields) and they appear.
 */
const loadEntityColumns = async ({ entityUuid, compositeEntityUuid }) => {
  const { columns: primaryColumns } = await listEntityFields({ entityUuid });
  const noMapping = {
    columns: primaryColumns,
    unsupportedColumns: EMPTY,
    recordKeyColumn: primaryColumns.find((column) => column.isRecordKey) ?? null,
  };
  if (!compositeEntityUuid) return noMapping;

  let mappedFields;
  try {
    ({ fields: mappedFields } = await getCompositeFields(compositeEntityUuid));
  } catch {
    return noMapping;
  }
  if (!mappedFields.length) return noMapping;

  /*
   * The neighbouring composites' fields (CT-5529).
   *
   * `composite-entity/relationship/list` answers which composites this one
   * can reach and how, already resolved from its own point of view. Every
   * mapped field of a reachable neighbour is offered, tagged with the
   * relationship that gets there — that tag is what lets the payload emit a
   * `COMPOSITE_RELATIONSHIP` join without ever asking the author for a route.
   *
   * A neighbour is a bonus, never a prerequisite: if the relationships cannot
   * be read, or a neighbour's mapping cannot, this composite's own fields are
   * unaffected.
   */
  const { relationships } = await listCompositeRelationships(compositeEntityUuid).catch(
    () => ({ relationships: EMPTY }),
  );
  const neighbourFields = (await Promise.all(relationships.map(
    (relationship) => getCompositeFields(relationship.compositeEntityUuid)
      .then(({ fields }) => fields.map((field) => ({
        ...field,
        origin: {
          ...field.origin,
          /*
           * `entityUuid` stays the **physical table** the field lives in.
           * The graph is a graph of entities: a source whose `entityUuid` is
           * a composite uuid is refused with
           * `DSW018 Entities [...] not found in this workspace` — measured,
           * because the obvious reading is that the source is the neighbour
           * composite.
           *
           * The heading is the composite's name, which is how the author
           * thinks of the neighbour. A neighbour that maps fields from two
           * of its own tables therefore yields two blocks under one name;
           * none does in dev, and splitting them is more honest than
           * pretending a physical table is a composite.
           */
          table: relationship.compositeName || field.origin?.table || '',
          role: 'RELATED_COMPOSITE',
          relationshipUuid: relationship.relationshipUuid,
          direction: relationship.direction,
          // A member uuid of the *neighbour* would be emitted as a `MEMBER`
          // join into a table this query never declares.
          memberUuid: '',
        },
      })))
      .catch(() => EMPTY),
  ))).flat();

  const allFields = [...mappedFields, ...neighbourFields];

  // Every physical table referenced, for the metadata a mapping does not
  // carry — a neighbour's field still lives in a table of its own.
  const otherMembers = [...new Set(
    allFields.map((field) => field.origin?.entityUuid).filter((uuid) => uuid && uuid !== entityUuid),
  )];
  const others = await Promise.all(otherMembers.map(
    (uuid) => listEntityFields({ entityUuid: uuid }).then(({ columns }) => [uuid, columns]).catch(() => [uuid, EMPTY]),
  ));

  return resolveMappedColumns({
    mappedFields: allFields,
    columnsByEntity: Object.fromEntries([[entityUuid, primaryColumns], ...others]),
    primaryEntityUuid: entityUuid,
    allowExtension: ALLOW_EXTENSION_MEMBER_FIELDS,
  });
};

/**
 * A published dataset's columns (CT-5860).
 *
 * A dataset answers which fields it carries and nothing about them — a
 * `fieldUuid` and the `fieldKey` it publishes them under. Everything the
 * builder needs beyond that (the label, the data type, the origin) is the
 * composite's, so this resolves the composite exactly as the entity path does
 * and then keeps only the fields the dataset actually exposes. Offering more
 * would let an author bind a column the query does not return.
 *
 * The composite's primary member is what `field/list` takes, and a dataset
 * does not name it, so the catalogue is where it comes from.
 */
const loadDatasetColumns = async (datasetUuid) => {
  const dataset = await getDataset(datasetUuid);
  const entities = await listAllEntities().catch(() => EMPTY);
  const composite = entities.find(
    (entity) => entity.compositeEntityUuid === dataset.compositeEntityUuid,
  );
  if (!composite?.primaryEntityUuid) {
    throw new Error('The entity behind this dataset is no longer available.');
  }

  const resolved = await loadEntityColumns({
    entityUuid: composite.primaryEntityUuid,
    compositeEntityUuid: dataset.compositeEntityUuid,
  });

  const published = new Set(
    dataset.columns.filter((column) => column.fieldUuid).map((column) => column.fieldUuid),
  );
  const carries = (column) => published.has(column.fieldUuid);

  return {
    columns: resolved.columns.filter(carries),
    unsupportedColumns: resolved.unsupportedColumns.filter(carries),
    recordKeyColumn: resolved.recordKeyColumn,
  };
};

/**
 * Columns of the currently selected dataset or entity (CT-5193, shared with
 * CT-5194 and CT-5195).
 *
 * The race-guard is keyed on the source, so switching from `Contacts` to `Deals`
 * cannot land Contacts' columns after the switch — which would leave filter rows
 * pointing at columns the new source does not have.
 *
 * Two sources, one backend now: an **entity** reads its fields from the
 * data-model aggregate, and a **dataset** reads its own column list and then
 * resolves the same fields through the composite behind it (CT-5860). The
 * dataset path used to be served from local seeds, which is why picking
 * `Existing dataset` produced a widget against columns nothing carried.
 *
 * `unsupportedColumns` is the set the builder lists but refuses to hand to a
 * filter, grouper or binding. It is empty while
 * `ALLOW_EXTENSION_MEMBER_FIELDS` is on — every mapped field is offered — and
 * the plumbing stays so turning the flag off restores the split in one line.
 *
 * @param {{ sourceKind?: 'dataset'|'entity', sourceId?: string,
 *           compositeEntityUuid?: string }} [options]
 */
export const useSourceColumns = ({ sourceKind, sourceId, compositeEntityUuid } = {}) => {
  const [state, setState] = useState({
    columns: EMPTY, unsupportedColumns: EMPTY, recordKeyColumn: null, error: null,
  });
  const [resolvedKey, setResolvedKey] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const fetchIdRef = useRef(0);

  const hasSource = Boolean(sourceKind && sourceId);
  const requestKey = `${sourceKind ?? ''}|${sourceId ?? ''}|${compositeEntityUuid ?? ''}|${reloadToken}`;
  // With no source there is nothing to wait for: an unconfigured Data step must
  // not render column skeletons forever.
  const isLoading = hasSource && resolvedKey !== requestKey;

  useEffect(() => {
    if (!hasSource) return undefined;

    const fetchId = ++fetchIdRef.current;
    let mounted = true;

    const load = sourceKind === 'entity'
      ? loadEntityColumns({ entityUuid: sourceId, compositeEntityUuid })
      : loadDatasetColumns(sourceId);

    load
      .then(({ columns, unsupportedColumns, recordKeyColumn }) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({ columns, unsupportedColumns, recordKeyColumn, error: null });
      })
      .catch((error) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({
          columns: EMPTY, unsupportedColumns: EMPTY, recordKeyColumn: null, error,
        });
      })
      .finally(() => {
        if (mounted && fetchId === fetchIdRef.current) setResolvedKey(requestKey);
      });

    return () => {
      mounted = false;
    };
  }, [compositeEntityUuid, hasSource, requestKey, sourceId, sourceKind]);

  /*
    Drop what this composite contributed to the cache, then ask again.

    One load here fans out across `listEntityFields`, `getCompositeFields` and
    `listCompositeRelationships`, all cached per entity (CT-5579). Only this
    composite's entries go: another one's columns are still good, and throwing
    them away would make a retry here cost a reload everywhere else.
  */
  const refetch = useCallback(() => {
    invalidateCompositeEntity(compositeEntityUuid);
    if (sourceId && sourceId !== compositeEntityUuid) invalidateCompositeEntity(sourceId);
    setReloadToken((n) => n + 1);
  }, [compositeEntityUuid, sourceId]);

  const columns = hasSource ? state.columns : EMPTY;
  const unsupportedColumns = hasSource ? state.unsupportedColumns : EMPTY;

  /**
   * Lookup by `fieldUuid`, so a row reads its column's type rather than
   * guessing it. Keyed by uuid, not `internalName`: `fieldUuid` is what every
   * selection (filters, groupers, exposed columns) is stored and sent as —
   * the backend resolves it against `mdl_entity_fields` with a uuid cast, and
   * a field's code there raises a Postgres error, not a validation one.
   */
  const columnsByFieldUuid = useMemo(
    () => Object.fromEntries(columns.map((column) => [column.fieldUuid, column])),
    [columns],
  );

  return useMemo(
    () => ({
      columns,
      unsupportedColumns,
      columnsByFieldUuid,
      isLoading,
      error: hasSource ? state.error : null,
      refetch,
    }),
    [
      columns, columnsByFieldUuid, hasSource, isLoading, refetch,
      state.error, unsupportedColumns,
    ],
  );
};

export default useSourceColumns;
